import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';
import { apiClient } from '@copro/api';
import { useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from '../../../../components/GlobalSnackbar/snackbarStore';
import { useGetTopicList } from '../../../topics/api/useGetTopicList';
import { keys as topicKeys } from '../../../topics/api/keys';

const formSchema = z.object({
  name: z.string().trim().min(1, 'Le nom du sujet est obligatoire'),
  topicId: z.string().min(1, 'Veuillez sélectionner un dossier parent'),
});

export type CreateSubTopicFormValues = z.infer<typeof formSchema>;

export type UseCreateSubTopicFromInboxModalProps = {
  open: boolean;
  onClose: () => void;
  initialTitle?: string | null;
  onSuccess: (newSubTopicId: string) => void;
};

export function useCreateSubTopicFromInboxModal(props: UseCreateSubTopicFromInboxModalProps) {
  const queryClient = useQueryClient();
  const { data: topics = [], isLoading: isLoadingTopics } = useGetTopicList(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<CreateSubTopicFormValues>({
    mode: 'onChange',
    defaultValues: {
      name: props.initialTitle || '',
      topicId: '',
    },
    resolver: zodResolver(formSchema),
  });

  // Pre-fill or default first topic when topics load
  useEffect(() => {
    if (props.open) {
      form.reset({
        name: props.initialTitle || '',
        topicId: topics[0]?.id || '',
      });
      setServerError(null);
    }
  }, [props.open, props.initialTitle, topics, form]);

  async function handleSubmit(values: CreateSubTopicFormValues) {
    setIsSubmitting(true);
    setServerError(null);

    try {
      const { data, error } = await apiClient
        .from('sub_topics')
        .insert({
          name: values.name.trim(),
          topic_id: values.topicId,
          state: 'pending',
        })
        .select('id')
        .single();

      if (error) {
        throw error;
      }

      enqueueSnackbar(`Sujet « ${values.name} » créé avec succès.`, 'success');

      // Invalidate subtopics cache so autocompletes update immediately
      queryClient.invalidateQueries({ queryKey: topicKeys.all });

      props.onSuccess(data.id);
      props.onClose();
    } catch (err: unknown) {
      console.error('Erreur lors de la création du sujet:', err);
      const message = err instanceof Error ? err.message : 'Erreur lors de la création du sujet';
      setServerError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return {
    form,
    topics,
    isLoadingTopics,
    isSubmitting,
    serverError,
    handleSubmit: form.handleSubmit(handleSubmit),
  };
}
